/**
 * Session facts the API client needs on every request. The CSRF synchronizer token lives in
 * memory only (02-api §2.1): it is set from `Me` after sign-in and on every `/auth/me`.
 */
let csrfToken: string | null = null

export function setCsrfToken(token: string | null): void {
  csrfToken = token
}

export function getCsrfToken(): string | null {
  return csrfToken
}

/** Per-tab id sent as `Tr-Client-Id`, so this tab can tell its own op batches from other people's. */
export const CLIENT_ID: string = crypto.randomUUID()

type Listener = (reason: SessionEndReason) => void
export type SessionEndReason = 'expired' | 'signed-out'

const listeners = new Set<Listener>()
const interceptors = new Set<(reason: SessionEndReason) => boolean>()
const renewals = new Set<() => void>()

/**
 * The session bus: a 401 anywhere, or `session.ended` on a stream, ends up here. The app's
 * SessionBridge navigates to sign-in; a screen with unsent work (the editor's op-log outbox) can
 * register an interceptor that returns true to show its own sign-in dialog instead. Signing in
 * again from that dialog announces `renewed`, so stopped streams and paused saves start again.
 */
export const sessionBus = {
  ended(reason: SessionEndReason = 'expired'): void {
    for (const intercept of interceptors) if (intercept(reason)) return
    for (const l of listeners) l(reason)
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
  intercept(handler: (reason: SessionEndReason) => boolean): () => void {
    interceptors.add(handler)
    return () => interceptors.delete(handler)
  },
  renewed(): void {
    for (const l of renewals) l()
  },
  onRenewed(listener: () => void): () => void {
    renewals.add(listener)
    return () => renewals.delete(listener)
  },
}
